import { HttpClient, HttpContext, HttpContextToken } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { ConfigService } from '@app/core/config/config.service';
import { PostalCodeInfo } from '@app/domain/models/postal-code-info';
import { PostalCodeRepository } from '@app/domain/ports/postal-code.repository';
import { mapSepomexToPostalCodeInfo, SepomexResponseDto } from '@app/data/mappers/postal-code.mapper';

/** Token de contexto HTTP que el `apiKeyInterceptor` puede leer para skip-ear
 *  el header `x-api-key` cuando se llama a un host externo (SEPOMEX). El
 *  interceptor lo decide; aquí solo lo marcamos. */
export const SKIP_API_KEY = new HttpContextToken<boolean>(() => false);

/** Timeout máximo para la consulta SEPOMEX. Si la API externa tarda más, el
 *  catchError retorna `null` y el componente deja al usuario capturar manual. */
const LOOKUP_TIMEOUT_MS = 3000;

/**
 * Implementación HTTP del `PostalCodeRepository` contra la API comunitaria de
 * SEPOMEX (api-sepomex.hckdrk.mx). La URL base se lee de `config.json` vía
 * `ConfigService.postalCodeApiUrl`. Si está vacía, el lookup retorna `null`
 * directamente — esto permite desactivar la funcionalidad por ambiente sin
 * tocar código.
 *
 * Diseño defensivo:
 * - Cualquier error (red, timeout, 4xx/5xx, shape inesperado) se atrapa y
 *   emite `null`. La UI muestra captura manual normal.
 * - Marca `SKIP_API_KEY=true` en el HttpContext para que el interceptor de
 *   `x-api-key` no envíe el header del backend a un tercero.
 */
@Injectable()
export class PostalCodeRemoteDataSource extends PostalCodeRepository {
  private readonly http = inject(HttpClient);
  private readonly configService = inject(ConfigService);

  override lookup(cp: string): Observable<PostalCodeInfo | null> {
    const baseUrl = this.configService.postalCodeApiUrl;
    if (!baseUrl) return of(null);
    const url = `${baseUrl.replace(/\/$/, '')}/${encodeURIComponent(cp)}`;
    const context = new HttpContext().set(SKIP_API_KEY, true);
    return this.http.get<SepomexResponseDto>(url, { context }).pipe(
      timeout(LOOKUP_TIMEOUT_MS),
      map((res) => mapSepomexToPostalCodeInfo(res, cp)),
      catchError(() => of(null)),
    );
  }
}
